import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/unsubscribe")({
  component: UnsubscribePage,
  head: () => ({
    meta: [
      { title: "Unsubscribe | SAFETY FIRST Winnipeg" },
      { name: "description", content: "Unsubscribe from SAFETY FIRST Winnipeg email notifications." },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:title", content: "Unsubscribe | SAFETY FIRST Winnipeg" },
      { property: "og:description", content: "Unsubscribe from SAFETY FIRST Winnipeg email notifications." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://safetyfirstmb.com/unsubscribe" }],
  }),
});

type State = "loading" | "ready" | "done" | "error";

function UnsubscribePage() {
  const [state, setState] = useState<State>("loading");
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("token");
    setToken(t);
    if (!t) {
      setError("This unsubscribe link is missing its token.");
      setState("error");
      return;
    }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(t)}`)
      .then((r) => (r.ok ? setState("ready") : Promise.reject(new Error("Invalid or expired link."))))
      .catch((e: Error) => {
        setError(e.message);
        setState("error");
      });
  }, []);

  const confirm = async () => {
    setState("loading");
    try {
      const res = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) throw new Error("We couldn't complete your request. Please try again.");
      setState("done");
    } catch (e) {
      setError((e as Error).message);
      setState("error");
    }
  };

  return (
    <main className="grid min-h-screen place-items-center bg-background px-6">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 text-center">
        <h1 className="font-display text-2xl text-foreground">Email preferences</h1>
        {state === "loading" && <p className="mt-3 text-sm text-muted-foreground">Checking your link…</p>}
        {state === "ready" && (
          <>
            <p className="mt-3 text-sm text-muted-foreground">
              Confirm you'd like to stop receiving emails from SAFETY FIRST Winnipeg.
            </p>
            <button
              onClick={confirm}
              className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              Confirm unsubscribe
            </button>
          </>
        )}
        {state === "done" && (
          <p className="mt-3 text-sm text-muted-foreground">You've been unsubscribed. Sorry to see you go.</p>
        )}
        {state === "error" && <p className="mt-3 text-sm text-primary">{error}</p>}
      </div>
    </main>
  );
}
